import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';

interface ApiResponse {
    isSuccess: boolean;
    statusCode: number;
    result: unknown;
    errors: { message?: string; Message?: string }[];
}

function extractErrorDetail(errors: unknown[], translate: TranslateService): string {
    const fallback = translate.instant('cropmap.common.something_went_wrong');
    if (!Array.isArray(errors) || errors.length === 0) return fallback;

    const messages = errors
        .map((err: any) => {
            const raw: string = err?.message ?? err?.Message ?? (typeof err === 'string' ? err : '');
            if (!raw) return null;
            const idx = raw.indexOf(':');
            return (idx >= 0 ? raw.slice(idx + 1) : raw).trim();
        })
        .filter(Boolean);

    return messages.length > 0 ? messages.join(', ') : fallback;
}

export const ApiToastInterceptor: HttpInterceptorFn = (req, next) => {
    const translate = inject(TranslateService);
    const authService = inject(AuthService);
    const toastService = inject(ToastService);

    const showErrorToast = (errors?: unknown[]) => {
        toastService.error({ detail: extractErrorDetail(errors ?? [], translate) });
    };

    return next(req).pipe(
        tap((event) => {
            if (!(event instanceof HttpResponse)) return;

            if (event.body && typeof event.body === 'object' && !(event.body instanceof Blob)) {
                const body = event.body as Record<string, unknown>;
                if ('isSuccess' in body) {
                    const apiBody = body as unknown as ApiResponse;
                    if (apiBody.statusCode === 401 || apiBody.statusCode === 403 || !authService.isAuthenticated()) return;
                    if (apiBody.isSuccess === false) {
                        showErrorToast(apiBody.errors);
                    }
                }
                return;
            }

            if (event.body instanceof Blob) {
                const reader = new FileReader();
                reader.onload = () => {
                    try {
                        const parsed = JSON.parse(reader.result as string) as Partial<ApiResponse>;
                        if (parsed && typeof parsed === 'object' && 'isSuccess' in parsed) {
                            if (parsed.statusCode === 401 || parsed.statusCode === 403) return;
                            if (parsed.isSuccess === false) {
                                showErrorToast(parsed.errors);
                            }
                        }
                    } catch { /* not JSON */ }
                };
                reader.readAsText(event.body);
            }
        }),
        catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status !== 401 && error.status !== 403) {
                const errorsArr = Array.isArray(error.error?.errors) ? error.error.errors : undefined;
                if (errorsArr?.length) {
                    showErrorToast(errorsArr);
                } else {
                    const raw = (error.error?.message ?? error.message ?? '') as string;
                    const idx = typeof raw === 'string' ? raw.indexOf(':') : -1;
                    const detail = typeof raw === 'string' && idx >= 0 ? raw.slice(idx + 1).trim() : raw;
                    toastService.error({ detail: detail || undefined });
                }
            } else if (!(error instanceof HttpErrorResponse)) {
                toastService.error();
            }
            return throwError(() => error);
        }),
    );
};
